<template>
  <div>
    <div v-if="loading" class="text-center mb-4">
      <v-progress-circular
        style="width: 64px; height: 64px"
        size="64"
      />
    </div>
    <v-row v-if="hasNoImportedFilesError">
      <v-col cols="12">
        <Alert
          type="error"
          :dismissible="false"
          icon
        >
          {{ $t("archives.steps.ArchivesAddStep1.lblDocumentRequired") }}
        </Alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <AppCard class="ArchivesAddStep1-card rounded-lg" elevation="0">
          <h3 class="text-center ArchivesAddStep1-card-title">
            {{ $t("archives.steps.ArchivesAddStep1.lblClosdFiles") }}
          </h3>
          <div class="d-flex flex-column ga-4">
            <img
              class="d-block mx-auto"
              src="@/assets/img/folder_file.svg"
              width="95"
              alt="Folder file"
            />
            <app-button
              type="outlined"
              variant="neutral"
              :disabled="loading"
              @click="openClosdFileBrowserModal"
            >
              {{ $t("archives.steps.ArchivesAddStep1.btnClosdFiles") }}
            </app-button>
          </div>
        </AppCard>
      </v-col>
      <v-col cols="12" md="6">
        <AppCard
          class="ArchivesAddStep1-card rounded-lg"
          elevation="0"
          @dragover.prevent
          @dragenter.prevent="dragInProgress = true"
          @dragleave.prevent="dragInProgress = false"
          @drop.prevent="onDrop"
        >
          <h3 class="text-center ArchivesAddStep1-card-title">
            {{ $t("archives.steps.ArchivesAddStep1.lblExternalFiles") }}
          </h3>
          <div class="d-flex flex-column align-center ga-4">
            <img src="@/assets/img/uploaderIllustration.svg" width="145" alt="Uploader Illustration" />
            <div class="d-flex flex-column align-center ga-1 text-text-secondary">
              <span class="font-weight-bold">
                {{ $t("archives.steps.ArchivesAddStep1.lblExternalFilesDrop") }}
              </span>
              <span class="">
                {{ $t("archives.steps.ArchivesAddStep1.lblExternalFilesDropOr") }}
              </span>
            </div>

            <input
              id="archive-file-loader"
              ref="archiveFileLoader"
              type="file"
              multiple
              class="d-none"
              @change="importFiles($event)"
            />
            <input
              id="archive-folder-loader"
              ref="archiveFolderLoader"
              type="file"
              webkitdirectory
              multiple
              style="display: none"
              @change="importFolderContent($event)"
            />
            <div class="d-flex flex-column align-center ga-4">
              <app-button
                type="outlined"
                variant="neutral"
                :disabled="loading"
                @click="
                () => {
                  this.$refs.archiveFileLoader.click();
                }
              "
              >
                {{ $t("archives.steps.ArchivesAddStep1.btnExternalFiles") }}
              </app-button>
              <app-button
                type="outlined"
                variant="neutral"
                :disabled="loading"
                @click="
                () => {
                  this.$refs.archiveFolderLoader.click();
                }
              "
              >
                {{ $t("archives.steps.ArchivesAddStep1.btnExternalFolder") }}
              </app-button>
            </div>
          </div>
        </AppCard>
      </v-col>
    </v-row>

    <div class="mt-4">
      <AppSelect :model-value="archivingConfig"
                   :items="archivingConfigOptionsItems"
                   item-title="duration"
                   :label="$t('archives.steps.ArchivesAddStep1.lblArchiveDuration')"
                   :error-messages="archivingConfigErrorMessage"
                   :hint="$t('archives.steps.ArchivesAddStep1.lblArchiveDurationDescription')"
                   persistent-hint
                   outlined
                   color="tertiary"
                   return-object
                   @update:model-value="$emit('archivingConfig', $event)"
        >
          <template #selection="{item}">
            {{ item.title }}
            <span v-t="{ path: 'archives.steps.ArchivesAddStep1.lblYears', plural: +item.title }"
                  style="margin-left: 2px"
            />
          </template>

          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #title>
                {{ item.title }}
                <span v-t="{ path: 'archives.steps.ArchivesAddStep1.lblYears', plural: +item.title }" />
              </template>
            </v-list-item>
          </template>
        </AppSelect>
    </div>

    <div v-if="importedFiles.length > 0" class="uploaded-file-list mt-4">
      <div class="mt-7 mb-4">
        <h3 class="text-text-primary text--small py-1 px-2">{{ $t('archives.steps.ArchivesAddStep1.selectedDocuments') }}</h3>
      </div>
      <ArchivesUploadedFileListItem v-for="(file, i) in importedFiles"
                                    :key="`file-${i}`"
                                    class="uploaded-file"
                                    :file="file"
                                    @delete-file="deleteFile"
      />
    </div>

    <ArchivesClosdFileBrowserDialog v-if="closdFileBrowserDialogIsOpened"
                                    @selectedFiles="importClosdFiles"
                                    @close="closdFileBrowserDialogIsOpened = false"
    />
  </div>
</template>

<script>
import { getFilesFromDataTransferItems } from 'datatransfer-files-promise'


import AppCard from '@/common/AppCard.vue'
import { formatImportedFile } from '@/common/utils/archives'
import { useArchivesStore } from '@/stores/archivesStore'

import ArchivesClosdFileBrowserDialog from './ArchivesClosdFileBrowserDialog.vue'
import ArchivesUploadedFileListItem from './ArchivesUploadedFileListItem.vue'
import Alert from '../../common/alerts/Alert.vue'
import AppSelect from '../../common/AppSelect.vue'

export default {
  name: 'ArchivesAddStep1',
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  components: {
    AppCard,
    Alert,
    AppSelect,
    ArchivesClosdFileBrowserDialog,
    ArchivesUploadedFileListItem,
  },
  props: {
    validator: {
      type: Object,
      required: true,
    },
    importedFiles: {
      type: Array,
      required: true,
    },
    archivingConfig: {
      required: true,
      validator: (value) => {
        return value === null || typeof value === 'object'
      },
    },
  },
  data () {
    return {
      dragInProgress: false,
      loading: false,
      closdFileBrowserDialogIsOpened: false,
    }
  },
  computed: {
    sections() {
      return this.archivesStore.sections
    },
    archivingConfigState () {
      if (!this.validator.archivingConfig.$anyDirty) {
        return true
      } else if (this.validator.archivingConfig.$anyError) {
        return false
      }
      return true
    },
    archivingConfigErrorMessage () {
      return this.archivingConfigState
        ? []
        : [this.$t('archives.steps.ArchivesAddStep1.lblArchiveDurationRequired')]
    },
    archivingConfigOptionsItems () {
      return (this.sections ?? []).map(archivingConfig => ({
        id: archivingConfig.id,
        duration: archivingConfig.duration,
      }))
    },
    hasNoImportedFilesError () {
      return this.validator.$dirty && this.validator.importedFiles.$error
    },
  },
  methods: {
    importFiles () {
      this.loading = true
      const queue = [...this.importedFiles]
      for (const f of this.$refs.archiveFileLoader.files) {
        queue.push(formatImportedFile(f, this.archivingConfig.id))
      }
      this.$emit('updateUploadedFiles', queue)
      this.loading = false
      this.$refs.archiveFileLoader.value = ''
    },
    importFolderContent () {
      this.loading = true
      for (const f of this.$refs.archiveFolderLoader.files) {
        this.importedFiles.push(formatImportedFile(f, this.archivingConfig.id))
      }
      this.loading = false
      this.$refs.archiveFolderLoader.value = ''
    },
    importClosdFiles (e) {
      this.loading = true
      for (const f of e) {
        this.importedFiles.push((formatImportedFile(f, this.archivingConfig.id)))
      }
      this.loading = false
    },
    async onDrop (e) {
      let files = e.dataTransfer ? e.dataTransfer.items : 'null'
      if (files) {
        this.loading = true
        const droppedFiles = await getFilesFromDataTransferItems(files)
        files = [...this.importedFiles, ...droppedFiles.map((file) => formatImportedFile(file, this.archivingConfig.id))]
        this.$emit('updateUploadedFiles', files)
        this.loading = false
      }
    },
    deleteFile (e) {
      try {
        const fileToDelete = this.importedFiles.findIndex(f => f === e)
        this.importedFiles.splice(fileToDelete, 1)
      } catch (e) {
        console.error(e)
      }
    },
    openClosdFileBrowserModal () {
      this.closdFileBrowserDialogIsOpened = true
    },
  },
}
</script>

<style scoped lang="scss">
.ArchivesAddStep1-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 400px;
  padding: 32px;
  gap: 32px;
}

.ArchivesAddStep1-card-title {
  color: rgb(var(--v-theme-text-primary));
  font-size: 16px;
  font-weight: 700;
}

.uploaded-file-list {
  & > .uploaded-file {
    padding-block: 6px;

    border-top: 1px solid rgb(var(--v-theme-border-primary));
    &:last-child {
      border-bottom: 1px solid rgb(var(--v-theme-border-primary));
    }
  }
}

:deep(.v-input__details) {
  .v-messages {
    color: rgb(var(--v-theme-text-tertiary));
    opacity: 1;
  }
}
</style>
